import { createContext, useContext, useEffect, useRef, useState, type MutableRefObject } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import type { Region } from "@/lib/meteoric-regions";

import type { TtuCatalog } from "../api";
import { channelLabel } from "../format";
import { PickerShell } from "./ProductPicker";

type Marketplace = TtuCatalog["marketplaces"][number];
const keyFor = (row: Marketplace) => `${row.channel}:${row.marketplace}`;

export const ChannelRefocusContext = createContext<MutableRefObject<boolean> | null>(null);

export function foldChannelSelection(value: string[], key: string, leaves: string[]): string[] {
  const branch = leaves.filter((leaf) => leaf.startsWith(`${key}:`));
  if (branch.length) {
    const withoutBranch = value.filter((item) => item !== key && !branch.includes(item));
    return value.includes(key) ? withoutBranch : [...withoutBranch, key];
  }
  const parent = key.split(":")[0]!;
  const withoutParent = value.filter((item) => item !== parent);
  return withoutParent.includes(key) ? withoutParent.filter((item) => item !== key) : [...withoutParent, key];
}

export function ChannelPicker({ marketplaces, region, value, onChange, disabled = false }: {
  marketplaces: Marketplace[];
  region: "overview" | Region;
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const refocus = useContext(ChannelRefocusContext);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!refocus?.current) return;
    triggerRef.current?.focus();
    refocus.current = false;
  }, [refocus]);
  const apply = (next: string[]) => {
    if (refocus) refocus.current = true;
    onChange(next);
  };
  return <PickerShell label="Channel" caption={value.length === 0 ? "Whole company" : value.length === 1 ? channelLabel(value[0]!) : `${value.length} selections`} open={open} setOpen={setOpen} disabled={disabled} triggerRef={triggerRef}>
    <ChannelPickerOptions marketplaces={marketplaces} region={region} value={value} onChange={apply} />
  </PickerShell>;
}

export function ChannelPickerOptions({ marketplaces, region, value, onChange }: {
  marketplaces: Marketplace[];
  region: "overview" | Region;
  value: string[];
  onChange: (value: string[]) => void;
}) {
  const visible = region === "overview" ? marketplaces : marketplaces.filter((row) => row.region === region);
  const leaves = visible.map(keyFor);
  const channels = [...new Set(visible.map((row) => row.channel))];
  const chosen = (key: string) => value.includes(key);
  const item = (key: string, label: string, inset = false) => <CommandItem
    data-nonmutating key={key} value={`${label} ${key}`} onSelect={() => onChange(foldChannelSelection(value, key, leaves))}
    className={inset ? "pl-7" : "font-semibold"}
  ><Checkbox data-nonmutating checked={chosen(key)} tabIndex={-1} aria-label={label} className="pointer-events-none" />{label}</CommandItem>;
  return <Command className="[&_[data-slot=command-input-wrapper]]:sr-only">
    <CommandInput data-nonmutating autoFocus aria-label="Channels" />
    <CommandList>
      <CommandItem data-nonmutating value="__whole__ Whole company" onSelect={() => onChange([])}><Checkbox data-nonmutating checked={value.length === 0} tabIndex={-1} className="pointer-events-none" />Whole company</CommandItem>
      {region !== "overview" && <p className="px-3 py-2 text-xs text-muted-foreground">Showing {region.toUpperCase()} marketplaces only</p>}
      {channels.map((channel) => <div key={channel}>{item(channel, channel)}{!chosen(channel) && visible.filter((row) => row.channel === channel).map((row) => item(keyFor(row), row.marketplace, true))}</div>)}
    </CommandList></Command>;
}
